/**
 * ContractualChargesList Component
 *
 * Displays contractual charges at the bottom of the category list.
 * Shows charges with their calculated totals and allows drag-to-reorder.
 */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { AddIcon } from 'utils/icons';
import {
  formatCurrency,
  formatPercentage,
  stopPropagation,
} from 'utils/helper';
import './styles.scss';

const ContractualChargesList = ({
  contractualCharges,
  onAddClick,
  onEdit,
  onDragEnd,
  hasEditAccess,
  budgetId,
  showAddInput,
  editingChargeId,
  renderEditForm,
  showAddButton = false, // New prop to control button visibility
}) => {
  // Track drag state to distinguish clicks from drags
  const dragStateRef = useRef({ isDragging: false, startX: 0, startY: 0 });

  if (!contractualCharges || contractualCharges.length === 0) {
    return (
      <div className="contractual-charges-list">
        {showAddButton && hasEditAccess() && !showAddInput && (
          <div className="swimlane-item swimlane-item--action">
            <div tabIndex="1" className="action-add-item" onClick={onAddClick}>
              Add Contractual Charge{' '}
              <AddIcon className="action-add-item__icon" />
            </div>
          </div>
        )}
      </div>
    );
  }

  const getDisplayName = charge => {
    if (charge.charge_type === 'percentage') {
      return `${charge.name} (${formatPercentage(charge.percentage)}%)`;
    }
    return `${charge.name} (Flat Fee)`;
  };

  const handleItemClick = (charge, e) => {
    // Don't trigger edit if:
    // - Already editing
    // - No edit access
    // - Was a drag operation
    if (
      editingChargeId === charge.id ||
      !hasEditAccess() ||
      dragStateRef.current.isDragging
    ) {
      return;
    }

    stopPropagation(e);
    onEdit(charge);
  };

  const handleMouseDown = e => {
    // Track initial mouse position to detect drags
    dragStateRef.current.startX = e.clientX;
    dragStateRef.current.startY = e.clientY;
    dragStateRef.current.isDragging = false;
  };

  const handleMouseMove = () => {
    // If mouse moved, it's likely a drag
    if (
      dragStateRef.current.startX !== 0 ||
      dragStateRef.current.startY !== 0
    ) {
      dragStateRef.current.isDragging = true;
    }
  };

  const handleMouseUp = () => {
    // Reset drag state after a short delay to allow click handler to check it
    setTimeout(() => {
      dragStateRef.current.isDragging = false;
      dragStateRef.current.startX = 0;
      dragStateRef.current.startY = 0;
    }, 100);
  };

  return (
    <div className="contractual-charges-list">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="contractual-charges-droppable">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {contractualCharges.map((charge, index) => {
                const itemClass =
                  index % 2 === 0
                    ? 'contractual-charge-item--even'
                    : 'contractual-charge-item--odd';

                const isEditing = editingChargeId === charge.id;

                return (
                  <Draggable
                    key={`contractual-charge-${charge.id}`}
                    draggableId={`contractual-charge-${charge.id}`}
                    index={index}
                    isDragDisabled={isEditing}
                  >
                    {draggableProvided => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                      >
                        {isEditing && renderEditForm ? (
                          renderEditForm(charge)
                        ) : (
                          <div
                            className={`contractual-charge-item ${itemClass}`}
                            {...draggableProvided.dragHandleProps}
                            onClick={e => handleItemClick(charge, e)}
                            onMouseDown={e => handleMouseDown(e)}
                            onMouseMove={e => handleMouseMove(e)}
                            onMouseUp={handleMouseUp}
                            style={{
                              cursor: hasEditAccess() ? 'pointer' : 'default',
                            }}
                          >
                            <div className="contractual-charge-item__info">
                              <div className="item-name-wrapper">
                                <span className="item-name">
                                  {getDisplayName(charge)}
                                </span>
                              </div>
                              <div className="item-cost">
                                {formatCurrency(charge.total_cost || 0)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {showAddButton && hasEditAccess() && !showAddInput && (
        <div className="swimlane-item swimlane-item--action">
          <div tabIndex="1" className="action-add-item" onClick={onAddClick}>
            Add Contractual Charge <AddIcon className="action-add-item__icon" />
          </div>
        </div>
      )}
    </div>
  );
};

ContractualChargesList.propTypes = {
  contractualCharges: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      charge_type: PropTypes.string,
      percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      flat_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      total_cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      position: PropTypes.number,
    })
  ),
  onAddClick: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  hasEditAccess: PropTypes.func.isRequired,
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showAddInput: PropTypes.bool,
  editingChargeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  renderEditForm: PropTypes.func,
  showAddButton: PropTypes.bool,
};

ContractualChargesList.defaultProps = {
  contractualCharges: [],
  budgetId: null,
  showAddInput: false,
  editingChargeId: null,
  renderEditForm: null,
  showAddButton: false,
};

export default ContractualChargesList;
